import { isC1cStudy, isCardioOneFacility } from '@/entities/study';
import { NormalButton, CustomButton } from '@/shared/ui/components';
import { AbortStudyIcon, FilledViewIcon, copyIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { zeroPad, dateToString, formatInitialDuration } from '@/shared/lib';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import type { UnassignedStudyCardProps } from './unassignedStudyCard.types';
import './unassignedStudyCard.scss';
import UnassignedStudyCardSkeleton from './unassignedStudyCardSkeleton/unassignedStudyCardSkeleton';

dayjs.extend(utc);

const UnassignedStudyCard = ({
  study,
  supportFacilities = [],
  isLoadedData,
  isShowFacilityName,
  handleClickStudyId,
  handleClickPatientName,
  handleAbortClick,
  handleClickCopy,
  handleGoToStudyManagement,
  handleAssignDeviceClick,
}: UnassignedStudyCardProps) =>
  isLoadedData ? (
    <div className="unassigned-study-card">
      <div className="header-container">
        <div className="left-container">
          <div className="title-block">
            <div className="title">{t`Study`}</div>
            {!_.isNil(study.studyFid) ? (
              <NormalButton
                id={`study-fid-btn-${study.studyFid}`}
                isText
                isBold
                buttonName={zeroPad(study.studyFid)}
                onClick={handleClickStudyId}
                contentClassName="content-btn"
              />
            ) : (
              <div>{DOUBLE_HYPHEN_BULLET}</div>
            )}
          </div>
          <div className="title-block">
            <div className="title">{t`Patient`}</div>
            <NormalButton
              id={`patient-name-study-btn-${study.studyFid}`}
              isText
              isBold
              buttonName={study.patient?.name}
              onClick={handleClickPatientName}
              contentClassName="content-btn"
            />
          </div>
          {isShowFacilityName && (
            <div className="title-block">
              <div className="title">{t`Facility`}</div>
              <div className="facility-name">{study.facility?.name}</div>
            </div>
          )}
        </div>
        <div className="right-container">
          {!isC1cStudy(study) && (
            <CustomButton
              id={`abort-study-btn-${study.studyFid}`}
              type="outline"
              style="danger"
              size="small"
              icon={<ReactSVG src={AbortStudyIcon} />}
              tooltipTitle={t`Abort study`}
              tooltipPlacement="bottom"
              onClick={handleAbortClick}
              ariaLabel={t`Abort study`}
            />
          )}
          {!isCardioOneFacility(study.facility?.id, supportFacilities) && (
            <CustomButton
              id={`copy-click-btn-${study.studyFid}`}
              type="outline"
              size="small"
              icon={<ReactSVG src={copyIcon} />}
              tooltipTitle={t`Copy study`}
              tooltipPlacement="bottom"
              onClick={handleClickCopy}
              ariaLabel={t`Copy study`}
            />
          )}
          <CustomButton
            id={`view-study-btn-${study.studyFid}`}
            type="outline"
            size="small"
            icon={<ReactSVG src={FilledViewIcon} />}
            tooltipTitle={t`Open study`}
            tooltipPlacement="bottom"
            onClick={handleGoToStudyManagement}
            ariaLabel={t`Open study`}
          />
          <CustomButton
            id="assign-device-btn"
            className="assign-device-btn"
            type="outline"
            size="small"
            onClick={handleAssignDeviceClick}
          >
            {t`Assign device`}
          </CustomButton>
        </div>
      </div>
      <div className="body-container">
        <div className="main-container left-main-container">
          <div className="left-sub-container">
            <div className="item-container">
              <span className="title">{`${t`Patient ID`}:`}</span>
              <span className="content">{study.patient?.patientId}</span>
            </div>
            <div className="item-container">
              <span className="title">{`${t`Patient return visit`}:`}</span>
              <span className="content">
                {!_.isEmpty(study.followOnStudy)
                  ? dateToString({
                      isoDate: dayjs.utc(study.followOnStudy.reportDueDate, 'YYYY-MM-DD'),
                      timezone: 'UTC+0',
                      format: 'MM/DD/YYYY',
                    })
                  : study.reportDueDate
                    ? dateToString({
                        isoDate: study.reportDueDate,
                        timezone: 'UTC+0',
                        format: 'MM/DD/YYYY',
                      })
                    : DOUBLE_HYPHEN_BULLET}
              </span>
            </div>
          </div>
          <div className="right-sub-container">
            <div className="item-container">
              <span className="title">{`${t`Study type`}:`}</span>
              <span className="content">{study.studyType}</span>
            </div>
            <div className="item-container">
              <span className="title">{`${t`Initial duration`}:`}</span>
              <span className="content">
                {!_.isNil(study.initialDurationOfStudy)
                  ? formatInitialDuration(study.initialDurationOfStudy)
                  : DOUBLE_HYPHEN_BULLET}
              </span>
            </div>
          </div>
        </div>
        <div className="main-container right-main-container">
          <div className="item-container">
            <span className="title">{`${t`Referring physician`}:`}</span>
            <span className="content">{study.referringPhysician?.name}</span>
          </div>
          <div className="item-container">
            <span className="title">{`${t`Interpreting physician`}:`}</span>
            <span className="content">{study.interprettingPhysician?.name}</span>
          </div>
        </div>
        <div className="main-container right-main-container-2">
          <div className="item-container">
            <span className="title">{`${t`Initiated time`}:`}</span>
            <span className="content">{study.initiatedTime}</span>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <UnassignedStudyCardSkeleton />
  );

export default UnassignedStudyCard;
